import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useInvitationByToken } from '@/hooks/useInvitations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export default function InviteAccept() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { data: invitation, isLoading, error } = useInvitationByToken(token || '');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const errorMessage = (code: string) => {
    switch (code) {
      case 'expired':
        return t('invite.errors.expired');
      case 'already_accepted':
        return t('invite.errors.alreadyAccepted');
      case 'invalid_token':
        return t('invite.errors.invalidToken');
      case 'weak_password':
        return t('invite.errors.weakPassword');
      default:
        return t('invite.errors.generic');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        title: t('common.error'),
        description: t('invite.errors.passwordMismatch'),
        variant: 'destructive',
      });
      return;
    }

    if (password.length < 8) {
      toast({
        title: t('common.error'),
        description: t('invite.errors.weakPassword'),
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Toujours repartir d'une session vierge : ne jamais réutiliser la session
      // d'un autre utilisateur (ex. l'admin qui a envoyé l'invitation).
      await supabase.auth.signOut({ scope: 'local' });

      const { data, error: fnError } = await supabase.functions.invoke('accept-invitation', {
        body: { token, password },
      });

      const code = (data as any)?.error;
      if (fnError || code) {
        toast({
          title: t('common.error'),
          description: errorMessage(code ?? 'generic'),
          variant: 'destructive',
        });
        return;
      }

      const invitedEmail = invitation.email.toLowerCase();
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: invitation.email,
        password,
      });

      const signedInEmail = signInData?.user?.email?.toLowerCase();

      if (signInError || !signedInEmail || signedInEmail !== invitedEmail) {
        await supabase.auth.signOut({ scope: 'local' });
        toast({
          title: t('invite.accountReady'),
          description: t('invite.pleaseSignIn'),
        });
        navigate('/auth');
        return;
      }

      toast({
        title: t('invite.welcome'),
        description: t('invite.accountConfigured'),
      });

      // Relire le rôle depuis le compte qui vient réellement d'être connecté.
      // Le rôle renvoyé par la fonction reste le repli pendant la très courte
      // fenêtre où les données du nouveau compte se propagent.
      let roleCode = (data as { role_code?: string } | null)?.role_code ?? invitation.roles?.code;
      for (let attempt = 0; attempt < 5; attempt += 1) {
        const { data: persistedRole } = await supabase.rpc('get_user_role', {
          _user_id: signInData.user.id,
        });
        if (persistedRole) {
          roleCode = persistedRole;
          break;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 250));
      }

      const dashboardByRole: Record<string, string> = {
        driver: '/driver/missions',
        shipper: '/shipper/dashboard',
        transporter: '/transporter/dashboard',
        admin: '/admin/dashboard',
      };
      const dashboardPath = dashboardByRole[roleCode ?? ''];
      if (!dashboardPath) {
        throw new Error('Invitation accepted without a valid user role');
      }

      // Confirmer que la session persistée appartient bien au nouvel utilisateur
      // avant de recharger directement son espace.
      const { data: persistedSession } = await supabase.auth.getSession();
      if (persistedSession.session?.user.id !== signInData.user.id) {
        throw new Error('The invited user session was not persisted');
      }

      window.location.assign(dashboardPath);
      return;

    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      toast({
        title: t('common.error'),
        description: t('invite.errors.generic'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#0880AE]" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="h-6 w-6 text-[#FF7171]" />
              <CardTitle className="text-[#37699E] font-['Lexend_Deca']">
                {t('invite.invalidTitle')}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <Alert className="border-[#FF7171]/20 bg-[#FF7171]/10">
              <AlertDescription className="text-[#2D3748]">
                {t('invite.invalidDescription')}
              </AlertDescription>
            </Alert>
            <Button
              onClick={() => navigate('/auth')}
              className="w-full mt-6 bg-[#0880AE] hover:bg-[#076A8E] text-white"
            >
              {t('invite.backToLogin')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="h-6 w-6 text-[#14A38B]" />
            <CardTitle className="text-[#37699E] font-['Lexend_Deca']">
              {t('invite.title')}
            </CardTitle>
          </div>
          <CardDescription className="text-[#2D3748]">
            {t('invite.subtitle')} <strong>{invitation.companies?.name}</strong>{' '}
            {t('invite.subtitleRole')} <strong>{invitation.roles?.name}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                {t('invite.email')}
              </Label>
              <Input id="email" type="email" value={invitation.email} disabled className="bg-[#EBF4F8]" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="first_name" className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                {t('invite.firstName')}
              </Label>
              <Input
                id="first_name"
                type="text"
                value={invitation.first_name || ''}
                disabled
                className="bg-[#EBF4F8]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="last_name" className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                {t('invite.lastName')}
              </Label>
              <Input
                id="last_name"
                type="text"
                value={invitation.last_name || ''}
                disabled
                className="bg-[#EBF4F8]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                {t('invite.password')}
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                placeholder={t('invite.passwordPlaceholder')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password" className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                {t('invite.confirmPassword')}
              </Label>
              <Input
                id="confirm_password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                placeholder={t('invite.confirmPasswordPlaceholder')}
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('invite.creating')}
                </>
              ) : (
                t('invite.createAccount')
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
